import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * DemoOverlay — guide-phrase renderer for the DEMO tutorial.
 *
 * Positioning model: the phrase is anchored at the VIEWPORT CENTER and
 * then shifted by `offset_x` / `offset_y` (both in pixels, positive X
 * right, positive Y down). Admins fine-tune the exact spot via four
 * arrow buttons in the backoffice.
 *
 *   phrase: {
 *     text: string, color: string, font_size: number,
 *     offset_x: number, offset_y: number,
 *     effect: "fade" | "slide" | "pulse" | "none",
 *   } | null
 *
 * The overlay is `pointer-events-none` so it never intercepts the
 * auto-played clicks happening underneath.
 *
 * IMPORTANT: positioning is split across two layers because framer-motion
 * animates `transform` and overrides any inline `transform` we set. The
 * outer `<div>` does the center + offset math via CSS `transform`; the
 * inner `<motion.div>` runs the fade/slide/pulse animation on top.
 */
export default function DemoOverlay({ phrase, containerClassName = "fixed inset-0 z-[70] pointer-events-none" }) {
  const initial =
    phrase?.effect === "slide"
      ? { opacity: 0, y: -30 }
      : phrase?.effect === "pulse"
      ? { opacity: 0, scale: 0.7 }
      : { opacity: 0 };

  const animate =
    phrase?.effect === "pulse"
      ? { opacity: 1, scale: [0.7, 1.15, 1] }
      : { opacity: 1, y: 0, scale: 1 };

  const exit =
    phrase?.effect === "slide"
      ? { opacity: 0, y: 30 }
      : { opacity: 0, scale: 0.95 };

  const ox = Number(phrase?.offset_x) || 0;
  const oy = Number(phrase?.offset_y) || 0;

  return (
    <div className={containerClassName} data-testid="demo-overlay">
      <AnimatePresence mode="wait">
        {phrase && (
          <div
            key={phrase.__id || phrase.text}
            // Outer positioner: viewport-centered then offset by (ox, oy).
            // Does NOT use motion so our transform here can't be clobbered
            // by framer-motion's animated transform on the inner node.
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: `translate(calc(-50% + ${ox}px), calc(-50% + ${oy}px))`,
            }}
            data-testid="demo-phrase-wrap"
          >
            <motion.div
              initial={initial}
              animate={animate}
              exit={exit}
              transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
              className="px-6 py-3 rounded-2xl bg-black/70 border-2 border-white/20 shadow-2xl backdrop-blur-sm max-w-[90vw]"
              data-testid="demo-phrase"
            >
              <div
                className="font-[Fredoka] font-extrabold text-center leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]"
                style={{
                  color: phrase.color || "#FDE047",
                  fontSize: `${phrase.font_size || 28}px`,
                  textShadow: "0 2px 12px rgba(0,0,0,0.85)",
                }}
              >
                {phrase.text}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
